import { translate } from "app/filters/i18nFilter";
import type {
    SignalEventsTypeItem,
    SignalEventType,
} from "app/pages/sales-intelligence/modules/company-analysis/services/signals/types";

import type {
    SignalEventDto } from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import {
    isSignalEventWithLink
} from "app/pages/sales-intelligence/modules/company-analysis/types/signals";
import useTooltipItemListCount from "app/pages/sales-intelligence/sub-modules/signals/hooks/signals-tooltip/useTooltipItemListCount";
import type { MouseEvent } from "react";

import type SignalEventsTooltipViewModel from "../../../../models/view/signals-tooltip/SignalEventsTooltipViewModel";
import SignalEventItem from "../../../shared/SignalEventItem/SignalEventItem";
import { StyledEventText } from "../../../shared/SignalEventItem/styles";
import SignalEventItemLink from "../../../shared/SignalEventItemLink/SignalEventItemLink";
import SignalTypeTag from "../../../shared/SignalTypeTag/SignalTypeTag";
import { StyledMainSectionWrapper, StyledSectionTitle, StyledTagsSectionWrapper, StyledTagsWrapper } from "./styles";

type Props = {
    createViewModel(itemsLimit: number): SignalEventsTooltipViewModel;
    getSignalTypeLink(eventType: SignalEventType): string;
    onSignalTypeClick(signalType: SignalEventType): void;
    onSignalArticleClick(signalType: SignalEventType, articleTitle: string): void;
};

const SignalsEventsList = (props: Props) => {
    const { createViewModel, getSignalTypeLink, onSignalTypeClick, onSignalArticleClick } = props;

    const maxItemsCount = useTooltipItemListCount();

    const viewModel = createViewModel(maxItemsCount);

    const handleTagClick = (event: MouseEvent<HTMLAnchorElement>, signalType: SignalEventType) => {
        event.stopPropagation();
        onSignalTypeClick(signalType);
    };

    const handleLinkClick = (signalEvent: Readonly<SignalEventDto>, articleTitle: string) => {
        const signalEventType = viewModel.getSignalEventType(signalEvent);

        onSignalArticleClick(signalEventType, articleTitle);
    };

    const renderSignalEvent = (signalEvent: Readonly<SignalEventDto>, index: number) => {
        const eventTitle = viewModel.matchEventTitle(signalEvent).trim();

        if (isSignalEventWithLink(signalEvent)) {
            return (
                <SignalEventItemLink
                    key={index}
                    signalEvent={signalEvent}
                    onLinkClick={() => handleLinkClick(signalEvent, eventTitle)}
                >
                    <StyledEventText>{eventTitle}</StyledEventText>
                </SignalEventItemLink>
            );
        }

        return (
            <SignalEventItem
                key={index}
                signalEvent={signalEvent}
            >
                <StyledEventText>{eventTitle}</StyledEventText>
            </SignalEventItem>
        );
    };

    const renderSignalEventsTag = ({ label, value }: Readonly<SignalEventsTypeItem>) => {
        const href = getSignalTypeLink(value);

        return (
            <a
                key={value}
                href={href}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => handleTagClick(event, value)}
            >
                <SignalTypeTag type={value}>{label}</SignalTypeTag>
            </a>
        );
    };

    return (
        <>
            <StyledMainSectionWrapper>{viewModel.signalEventsList.map(renderSignalEvent)}</StyledMainSectionWrapper>

            {viewModel.signalEventsTypeItems.length > 0 ? <StyledTagsSectionWrapper>
                    <StyledSectionTitle>
                        {translate("si.signals.table-columns.tooltip.more-signals")}
                    </StyledSectionTitle>

                    <StyledTagsWrapper>{viewModel.signalEventsTypeItems.map(renderSignalEventsTag)}</StyledTagsWrapper>
                </StyledTagsSectionWrapper> : null}
        </>
    );
};

export default SignalsEventsList;
